import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import "../styles/Home.css";
import BackToTop from "./BackToTop";

/* ── Services list shown in hero terminal ── */
const TERMINAL_LINES = [
  { delay: 0,    text: "$ initializing codix.services..." },
  { delay: 600,  text: "> Web Development" },
  { delay: 1000, text: "> Mobile App Development" },
  { delay: 1400, text: "> Custom Systems & Dashboards" },
  { delay: 1800, text: "> SaaS Product Development" },
  { delay: 2200, text: "> AI Integration & Automation" },
  { delay: 2600, text: "> SEO & Digital Growth" },
  { delay: 3000, text: "> Hosting, Domains & Support" },
  { delay: 3400, text: "✓ All systems ready." },
];

/* ── Typewriter hook ── */
function useTypewriter(text, speed = 38, startDelay = 0) {
  const [displayed, setDisplayed] = useState("");
  useEffect(() => {
    let idx = 0;
    let timer;
    const start = setTimeout(() => {
      timer = setInterval(() => {
        idx++;
        setDisplayed(text.slice(0, idx));
        if (idx >= text.length) clearInterval(timer);
      }, speed);
    }, startDelay);
    return () => { clearTimeout(start); clearInterval(timer); };
  }, [text, speed, startDelay]);
  return displayed;
}

/* ── Terminal component ── */
function Terminal() {
  const [lines, setLines] = useState([]);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    TERMINAL_LINES.forEach(({ delay, text }) => {
      setTimeout(() => {
        setLines((prev) => [...prev, text]);
      }, delay);
    });
  }, []);

  const isSuccess = (l) => l.startsWith("✓");
  const isCommand = (l) => l.startsWith("$");

  return (
    <div className="terminal" role="presentation" aria-hidden="true">
      <div className="terminal-titlebar">
        <span className="t-dot t-red" />
        <span className="t-dot t-yellow" />
        <span className="t-dot t-green" />
        <span className="t-title">codix — services.sh</span>
      </div>
      <div className="terminal-body">
        {lines.map((line, i) => (
          <div
            key={i}
            className={`t-line ${isSuccess(line) ? "t-success" : ""} ${isCommand(line) ? "t-command" : ""}`}
          >
            {line}
          </div>
        ))}
        {lines.length < TERMINAL_LINES.length && (
          <span className="t-cursor">█</span>
        )}
      </div>
    </div>
  );
}

/* ── Stats ── */
const STATS = [
  { value: "50+",  label: "Projects Delivered" },
  { value: "3+",   label: "Years in Business" },
  { value: "100%", label: "Client Satisfaction" },
  { value: "24/7", label: "Support & Maintenance" },
];

function Home() {
  const heroTitle = useTypewriter("We Build Websites That Work.", 52, 300);
  const [heroIn, setHeroIn] = useState(false);
  const statsRef = useRef(null);
  const [statsVis, setStatsVis] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroIn(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setStatsVis(true); },
      { threshold: 0.2 }
    );
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Codix Tech | Web Development Agency Lebanon</title>
        <meta
          name="description"
          content="Codix Tech — Lebanon's premier web development agency. We build websites, mobile apps, SaaS platforms, and AI-powered systems that drive real business growth."
        />
        <meta
          name="keywords"
          content="Web Development Lebanon, Mobile App Development, SaaS Development, AI Integration, SEO Lebanon, Codix Tech"
        />
        <meta name="author" content="Codix Tech" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Codix Tech",
            "url": "https://www.codixlb.com",
            "logo": "https://www.codixlb.com/logo.png",
            "sameAs": [
              "https://www.facebook.com/share/17fNY6Dqdk",
              "https://instagram.com/codix.lb",
              "https://wa.me/96181233235"
            ]
          }
        `}</script>
      </Helmet>

      <section id="home" className="home-section">
        {/* background grid */}
        <div className="home-grid" aria-hidden="true">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="home-grid-col" />
          ))}
        </div>

        {/* orange accent bar */}
        <div className="home-accent-bar" aria-hidden="true" />

        <div className={`home-inner ${heroIn ? "home-inner--in" : ""}`}>

          {/* ── LEFT: text ── */}
          <div className="home-text">
            {/* eyebrow */}
            <div className="home-eyebrow">
              <span className="eyebrow-dot" aria-hidden="true" />
              <span className="eyebrow-mono">&lt;Codix/&gt;</span>
              <span className="eyebrow-label">— Web Development Agency</span>
            </div>

            {/* heading */}
            <h1 className="home-title">
              {heroTitle}
              <span className="home-cursor" aria-hidden="true">_</span>
            </h1>

            {/* sub */}
            <p className="home-subtitle">
              We design, build, and scale modern digital products — from corporate
              websites to full-stack SaaS platforms, mobile apps, and AI-powered
              systems. Every line of code is built for performance, growth, and results.
            </p>

            {/* service pills */}
            <div className="home-pills">
              {[
                "Web Dev", "Mobile Apps", "SaaS", "AI & Automation",
                "Systems", "SEO", "Hosting"
              ].map((s) => (
                <span key={s} className="home-pill">{s}</span>
              ))}
            </div>

            {/* CTAs */}
            <div className="home-actions">
              <a
                href="https://book.codixlb.com"
                target="_blank"
                rel="noopener noreferrer"
                className="home-btn-primary"
              >
                Book Free Consultation
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a href="#services" className="home-btn-ghost">
                Our Services
              </a>
            </div>
          </div>

          {/* ── RIGHT: terminal ── */}
          <div className="home-visual">
            <Terminal />
            <div className="terminal-glow" aria-hidden="true" />
          </div>
        </div>

        {/* ── Stats bar ── */}
        <div
          className={`home-stats ${statsVis ? "home-stats--vis" : ""}`}
          ref={statsRef}
        >
          {STATS.map(({ value, label }, i) => (
            <div
              key={label}
              className="stat-item"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <span className="stat-value">{value}</span>
              <span className="stat-label">{label}</span>
            </div>
          ))}
        </div>

        {/* scroll cue */}
        <div className="scroll-cue" aria-hidden="true">
          <div className="scroll-line" />
          <span>Scroll</span>
        </div>
      </section>

      <BackToTop />
    </>
  );
}

export default Home;
